import { useState, type FormEvent } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/dah954joh653ivfvnoug";
const MAX_DETAIL = 500;

interface FormResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

export default function TipForm() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [detailLength, setDetailLength] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const payload = new FormData(form);

    const honeypot = String(payload.get("contact_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      return;
    }

    setStatus("submitting");
    setFormError("");

    const body = new URLSearchParams();
    payload.forEach((value, key) => {
      if (key === "contact_alt") return;
      body.append(key, String(value));
    });

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await response.text();

      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ?? parsed?.message ?? parsed?.meta?.detail ?? responseText ?? "";
      const isSpam = serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
        setDetailLength(0);
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交没有成功，请稍后再试一次。");
      }
    } catch {
      setStatus("error");
      setFormError("网络连接出现问题，请检查网络后重试。");
    }
  };

  return (
    <form
      id="tip-form"
      data-readdy-form="tip"
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8"
    >
      <h3 className="font-heading text-lg font-bold text-foreground-950">投递一条线索</h3>
      <p className="mt-2 text-sm leading-7 text-foreground-600">
        尽量附上可公开查证的链接或时间点。我们会先核对，再决定是否做成完整的复盘档案。
      </p>

      <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="tip-name" className="block text-sm font-medium text-foreground-800">
            称呼
          </label>
          <input
            id="tip-name"
            name="name"
            type="text"
            required
            placeholder="怎么称呼你都可以"
            className="mt-2 h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400 placeholder:text-foreground-400"
          />
        </div>
        <div>
          <label htmlFor="tip-email" className="block text-sm font-medium text-foreground-800">
            邮箱
          </label>
          <input
            id="tip-email"
            name="email"
            type="email"
            required
            placeholder="用于我们回复核对结果"
            className="mt-2 h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400 placeholder:text-foreground-400"
          />
        </div>
      </div>

      <div className="mt-5">
        <label htmlFor="tip-topic" className="block text-sm font-medium text-foreground-800">
          线索类型
        </label>
        <select
          id="tip-topic"
          name="topic"
          defaultValue="影视剧集"
          className="mt-2 h-11 w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400"
        >
          <option value="影视剧集">影视剧集</option>
          <option value="综艺幕后">综艺幕后</option>
          <option value="商业代言">商业代言</option>
          <option value="演出 Live">演出 Live</option>
          <option value="娱乐圈动态">娱乐圈动态</option>
          <option value="其他">其他</option>
        </select>
      </div>

      <div className="mt-5">
        <label htmlFor="tip-detail" className="block text-sm font-medium text-foreground-800">
          线索说明
        </label>
        <textarea
          id="tip-detail"
          name="detail"
          required
          rows={5}
          maxLength={MAX_DETAIL}
          onChange={(e) => setDetailLength(e.target.value.length)}
          placeholder="描述一下事件的核心争议、关键时间点，以及你手上有哪些可核实的公开信息。"
          className="mt-2 w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-3 text-sm leading-7 text-foreground-950 outline-none transition-colors focus:border-primary-400 placeholder:text-foreground-400"
        />
        <p className="mt-1.5 text-right text-xs text-foreground-500">
          {detailLength} / {MAX_DETAIL}
        </p>
      </div>

      <input
        type="text"
        name="contact_alt"
        className="form-hp"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <button
          type="submit"
          disabled={status === "submitting"}
          className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"}></i>
          {status === "submitting" ? "正在提交" : "提交线索"}
        </button>
        <p className="text-xs leading-6 text-foreground-500">
          我们不会公开你的联系方式，也不会采用无法核实的私下说法。
        </p>
      </div>

      {status === "success" && (
        <p className="mt-5 flex items-center gap-2 rounded-md bg-secondary-50 px-4 py-3 text-sm text-secondary-800">
          <i className="ri-checkbox-circle-line text-lg"></i>
          线索已收到，我们会尽快核对。需要回复的会通过邮箱联系你。
        </p>
      )}
      {status === "error" && (
        <p className="mt-5 flex items-center gap-2 rounded-md bg-primary-50 px-4 py-3 text-sm text-primary-800">
          <i className="ri-error-warning-line text-lg"></i>
          {formError}
        </p>
      )}
    </form>
  );
}